import React from 'react';
import type {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  UseFormReturn
} from 'react-hook-form';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import type { VariableSettingsDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';

type VariablesFieldArray = FieldArrayWithId<ScenarioSettingsType, 'variables', 'key'>[];

export type ScenarioVariablesFormContextValue = {
  methods: UseFormReturn<ScenarioSettingsType>;
  variables: VariableSettingsDto[];
  fields: VariablesFieldArray;
  append: UseFieldArrayAppend<ScenarioSettingsType, 'variables'>;
  update: UseFieldArrayUpdate<ScenarioSettingsType, 'variables'>;
  remove: UseFieldArrayRemove;
  replace: UseFieldArrayReplace<ScenarioSettingsType, 'variables'>;
};

const ScenarioVariablesFormContext = React.createContext<ScenarioVariablesFormContextValue | null>(null);

export const ScenarioVariablesFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const methods = useFormContext<ScenarioSettingsType>();
  const variables = useWatch({
    control: methods.control,
    name: 'variables'
  }) ?? [];

  const fieldArray = useFieldArray({
    name: 'variables',
    control: methods.control,
    keyName: 'key'
  });

  const value = React.useMemo<ScenarioVariablesFormContextValue>(() => {
    return {
      methods,
      variables,
      fields: fieldArray.fields,
      append: fieldArray.append,
      update: fieldArray.update,
      remove: fieldArray.remove,
      replace: fieldArray.replace
    };
  }, [
    methods,
    variables,
    fieldArray.fields,
    fieldArray.append,
    fieldArray.update,
    fieldArray.remove,
    fieldArray.replace
  ]);

  return (
    <ScenarioVariablesFormContext.Provider value={value}>
      {children}
    </ScenarioVariablesFormContext.Provider>
  );
};

export const useScenarioVariablesFormContext = (): ScenarioVariablesFormContextValue | null => {
  return React.useContext(ScenarioVariablesFormContext);
};
